import { ApolloError } from "@apollo/client";
import { CompanyAccess, MenuStatusType } from "@me-team/host/main/graphql/types";
import { MenuFormSubmitHandler, PartialMenu } from "../../../../interfaces/meMenuInterfaces/interfaces";
import {
    useCloneMenuMutation,
    useCreateMenuMutation,
    useRestoreMenuMutation,
    useUpdateMenuMutation,
} from "@me-team/host/main/menu/graphql/menu.hooks";
import { useNavigate, useParams } from "react-router-dom";
import { makeMenuInput } from "../helpers";
import { useTranslation } from "react-i18next";
import { cloneNameToApi } from "../../utils/cloneNameUtils";
import { useRoleAccess } from "@me-team/host/src/utils/useRoleAccess";
import { canEditMenuContentByStatus } from "../menuEnumMappings";

export type AsyncFn = () => Promise<unknown>;

export const useMenuSubmitters = (opts: {
    onClose: () => void;
    refetch?: AsyncFn | readonly AsyncFn[];
    onSuccess?: (text: string) => void;
}) => {
    const { t } = useTranslation();
    const { branchId } = useParams();
    const navigate = useNavigate();
    const { onClose, refetch } = opts;

    const [createMenu] = useCreateMenuMutation();
    const [updateMenu] = useUpdateMenuMutation();
    const [cloneMenu] = useCloneMenuMutation();
    const [restoreMenu] = useRestoreMenuMutation();
    const canCreateMenu = useRoleAccess(CompanyAccess.MenuCreate);
    const canCloneEditMenu = useRoleAccess(CompanyAccess.MenuCloneEdit);
    const canDeleteRestoreMenu = useRoleAccess(CompanyAccess.MenuDeleteRestore);

    const callRefetches = async () => {
        if (!refetch) return;
        const fns = Array.isArray(refetch) ? refetch : [refetch];
        await Promise.all(fns.map((f) => f()));
    };

    const isDuplicateMenuNameError = (error: ApolloError): boolean => {
        const graphQLErrors = error?.graphQLErrors || [];
        if (!graphQLErrors.length) return false;

        return graphQLErrors.some((err) => {
            const category = (err.extensions as any)?.category;

            return category === "menu.already_exists";
        });
    };

    const wrap =
        (
            fn: MenuFormSubmitHandler,
            canSubmit: boolean | ((context: { menu?: PartialMenu }) => boolean) = true
        ): MenuFormSubmitHandler =>
        async (values, helpers, context) => {
            const isAllowed = typeof canSubmit === "function" ? canSubmit(context) : canSubmit;

            if (!isAllowed) {
                helpers.setSubmitting(false);
                return;
            }

            try {
                await fn(values, helpers, context);
                await callRefetches();
                onClose();
            } catch (error) {
                const apolloError = error as ApolloError;
                if (isDuplicateMenuNameError(apolloError)) {
                    helpers.setFieldError("name", t("A menu with this name already exists"));
                } else {
                    console.error("error", error);
                }
            } finally {
                helpers.setSubmitting(false);
            }
        };

    return {
        create: wrap(async (values) => {
            const normalizedName = cloneNameToApi(values.name, t);

            const input = makeMenuInput(
                { ...values, name: normalizedName },
                { status: MenuStatusType.UnderDevelopment }
            );
            await createMenu({
                variables: { branchId: Number(branchId), input },
                context: { errorType: "local" },
                onCompleted: (data) => {
                    const newMenuId = data?.createMenu?.id;
                    newMenuId &&
                        navigate(`/menu/page/${newMenuId}/branch/${branchId}`, {
                            state: { text: t("Created"), toast: true },
                        });
                },
            });
        }, canCreateMenu),
        edit: wrap(
            async (values, _helpers, { menu }) => {
                if (!menu) return;
                const input = makeMenuInput(values);
                await updateMenu({
                    variables: { menuId: menu.id, branchId: Number(branchId), input },
                    onCompleted: () => opts.onSuccess?.(t("Saved")),
                });
            },
            ({ menu }) => canEditMenuContentByStatus(menu?.status?.slug, canCreateMenu, canCloneEditMenu)
        ),
        clone: wrap(async (values, _helpers, { menu }) => {
            if (!menu) return;
            const normalizedName = cloneNameToApi(values.name, t);
            const input = makeMenuInput(
                { ...values, name: normalizedName },
                { status: MenuStatusType.UnderDevelopment }
            );

            await cloneMenu({
                variables: { cloneMenuId: menu.id, branchId: Number(branchId), input },
                onCompleted: () => opts.onSuccess?.(t("Saved")),
            });
        }, canCloneEditMenu),
        restore: wrap(async (_values, _helpers, { menu }) => {
            if (!menu) return;

            await restoreMenu({
                variables: { id: menu.id, branchId: Number(branchId) },
                onCompleted: () => opts.onSuccess?.(t("Restored")),
            });
        }, canDeleteRestoreMenu),
    };
};
